import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useSelector, useDispatch } from "react-redux";
import { useState } from "react";
import PositiveOrNegetiveSTSkeleton from "../../../../../SearchTermSkeletons/PositiveOrNegetiveSTSkeleton";
import {
  setNegativeKeywordDefinitions,
  removeNegativeDefinition,
} from "../../../../../../../../../features/aiRecomendationTabSlice";

// Example options for the autocomplete
const definitionOptions = [
  { label: "Remove Negative", value: "mark-as-removed" },
];

const SecondNegativeST = ({ open, rowData, onClose }: any) => {
  const dispatch = useDispatch();
  const {
    positiveSTLoader,
    negativeKeywordDetails,
    selectedNegativeKeywordDefinitions,
  } = useSelector((state: any) => state.aiRecomandationTab);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Map negativeKeywordDetails to rows
  const rows =
    negativeKeywordDetails?.map((item: any, index: number) => ({
      id: index + 1,
      keyword_shared_uid: item.keyword_shared_uid,
      keyword_id: item.keyword_id,
      search_term_shared_uid: item.search_term_shared_uid,
      keywordName: item.keyword_name || "",
      matchType: item.keyword_match_type || "",
      adgroupName: item.adgroup_name || "",
      adgroup_shared_uid: item.adgroup_shared_uid,
      campaignName: item.campaign_name || "",
      lastSeen: item.last_seen || "",
      imp: item.impressions_last_3_years ?? 0,
      clicks: item.clicks_last_3_years ?? 0,
      cost: `${item.cost_last_3_years ?? 0}$`,
      cvr: `${Math.round(Number(item.conversions_last_3_years ?? 0) * 100)}%`,
    })) || [];

  // Define columns
  const columns: GridColDef[] = [
    {
      field: "keywordName",
      headerName: "Keyword Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "matchType",
      headerName: "Match Type",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "adgroupName",
      headerName: "Adgroup Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "campaignName",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "lastSeen",
      headerName: "Last Seen",
      flex: 0.7,
      minWidth: 120,
    },
    {
      field: "imp",
      headerName: "IMP",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "clicks",
      headerName: "Clicks",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "cost",
      headerName: "Cost",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "cvr",
      headerName: "CVR",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "definition",
      headerName: "Definition",
      flex: 0.8,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        // Create a composite key: search_term + keyword_shared_uid
        const keywordSharedUid = params.row.keyword_shared_uid || params.row.id;
        const compositeKey = `${rowData?.search_term}_${keywordSharedUid}`;

        // Get the current selected value from Redux state using composite key
        const selectedDefinition =
          selectedNegativeKeywordDefinitions?.[compositeKey]?.definition;
        const selectedOption = definitionOptions.find(
          (opt) => opt.value === selectedDefinition
        );

        return (
          <Autocomplete
            size="small"
            options={definitionOptions}
            value={selectedOption || null}
            getOptionLabel={(option) => option.label}
            onChange={(_, selectedOption) => {
              if (selectedOption) {
                // Set the definition when an option is selected
                dispatch(
                  setNegativeKeywordDefinitions({
                    id: compositeKey, // Use composite key
                    row: {
                      ...params.row,
                      search_term: rowData?.search_term,
                      search_term_match_type: rowData?.search_term_match_type,
                    },
                    definition: selectedOption.value,
                  })
                );
              } else {
                // Remove the definition when cleared
                dispatch(removeNegativeDefinition(compositeKey));
              }
            }}
            sx={{ width: "100%" }}
            renderInput={(paramsInput) => (
              <TextField
                {...paramsInput}
                placeholder="Select..."
                variant="standard"
                color="secondary"
                InputProps={{
                  ...paramsInput.InputProps,
                  disableUnderline: true,
                }}
                data-tracker="617"
              />
            )}
          />
        );
      },
    },
  ];

  if (!rowData) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            onClose();
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{rowData?.search_term} - Negative Keyword</DialogTitle>
        <DialogContent>
          {positiveSTLoader ? (
            <PositiveOrNegetiveSTSkeleton />
          ) : (
            <Box
              sx={{
                width: "100%",
                mt: 1,
                height: `${Math.min(rows.length * 52 + 56, 500)}px`,
              }}
            >
              <DataGridPro
                sx={{
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                    { outline: "none" },
                  "& .MuiDataGrid-cellEmpty": {
                    width: "auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#fff",
                  },
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                    "&:hover": {
                      backgroundColor: "rgba(156, 39, 176, 0.16) !important",
                    },
                  },
                  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft":
                    {
                      backgroundColor: "#f0def3 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft":
                    {
                      backgroundColor: "#e6d6e9ff !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#f0def3 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#e6d6e9ff !important",
                    },
                  "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#ffffff",
                    },
                  "& .MuiDataGrid-cell.Mui-selected": {
                    backgroundColor: "inherit !important",
                  },
                  "& .MuiCheckbox-root": {
                    color: "#9c27b0 !important",
                  },
                  "& .MuiCheckbox-root.Mui-checked": {
                    color: "#9c27b0",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    minHeight: "52px !important",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    minHeight: "auto !important",
                  },
                  border: "none",
                }}
                autoHeight
                density="compact"
                rows={positiveSTLoader ? [] : rows}
                columns={columns}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                disableColumnMenu
                disableColumnFilter
                disableColumnReorder
                sortingOrder={["asc", "desc"]}
                isCellEditable={() => false}
                disableRowSelectionOnClick
                initialState={{
                  pinnedColumns: {
                    left: ["keywordName", "matchType"],
                    right: ["definition"],
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 20px 16px" }}>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SecondNegativeST;
